import { useQuery } from '@apollo/client';

import { useCurrentUserContext } from 'utils/User';
import { FEATURE_FLAGS } from './constants';

import validFeatureFlagQuery from './validFeatureFlag.graphql';

/**
 * Returns the flag value together with whether it has resolved yet. `resolved`
 * is false while the query is still in flight with no value, which lets callers
 * avoid acting on the default `false` before the real value is known.
 */
export function useFeatureFlagStatus(flag: ObjectValues<typeof FEATURE_FLAGS>) {
  const { isLoggedIn } = useCurrentUserContext();

  const { data, loading } = useQuery<
    ValidFeatureFlagQuery,
    ValidFeatureFlagQueryVariables
  >(validFeatureFlagQuery, {
    skip: !isLoggedIn,
    fetchPolicy: 'cache-and-network',
    variables: { flag },
  });

  const valid = data?.validFeatureFlag?.valid ?? false;
  // Resolved once we have a value (from cache or network), or once a query
  // that will never run (skipped) has settled.
  const resolved = !isLoggedIn || data !== undefined || !loading;

  return { valid, resolved };
}

export default function useFeatureFlag(
  flag: ObjectValues<typeof FEATURE_FLAGS>,
) {
  return useFeatureFlagStatus(flag).valid;
}

export { FEATURE_FLAGS };
